/** Cross-task authoring board; job details stay in a panel over the list. */
import { useCallback, useEffect, useState } from 'react'
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom'
import { api } from '@/api/client'
import { Icon } from '@/components/Icon'
import { useBoardData } from '@/components/useBoardData'
import { isLive, isPending } from '@/components/agent/job'
import { ActiveAgent, AgentHistory, needsAttention } from '@/components/agent/AgentJobs'
import type { AuthorMessage } from '@/components/agent/board'
import type { BoardJob } from '@/components/agent/types'
import { AgentPanel } from './AgentPanel'
import AgentMessages from './AgentMessages'
import './runs.css'
import './agents.css'

export default function Agents() {
  const state = useBoardData<{ jobs: BoardJob[] }>('/eng/agents')
  const [search, setSearch] = useState('')
  const [messagesByTask, setMessagesByTask] = useState<Record<string, AuthorMessage[] | null>>({})
  const { taskId, jobId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  // Polling the board must not refocus the panel while a developer is typing.
  const closePanel = useCallback(() => navigate('/eng/agents'), [navigate])
  const messagesView = location.pathname === '/eng/agents/messages'
  const taskIds = [...new Set((state.data?.jobs ?? []).map(job => job.task_id))].sort().join(',')
  useEffect(() => {
    const agent = new URLSearchParams(location.search).get('agent')
    if (agent && !messagesView) setSearch(agent)
  }, [location.search, messagesView])
  useEffect(() => {
    const timer = window.setInterval(() => { if (!document.hidden) state.reload() }, 15000)
    return () => clearInterval(timer)
  }, [state.reload])
  useEffect(() => {
    if (messagesView || !taskIds) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout>
    const refresh = async () => {
      if (!document.hidden) {
        const tasks = taskIds.split(',')
        const results: Record<string, AuthorMessage[] | null> = {}
        // Avoid one simultaneous Lambda invocation per task. Poll again only
        // after this batch finishes, so slow reads cannot build up more requests.
        const worker = async () => {
          while (tasks.length && !cancelled) {
            const task = tasks.shift()!
            try {
              const result = await api.get<{ items: AuthorMessage[] }>(`/eng/agent-messages/author/${encodeURIComponent(task)}`, { limit: 2 })
              results[task] = result.items
            } catch { results[task] = null }
          }
        }
        await Promise.all([worker(), worker()])
        if (!cancelled) setMessagesByTask(previous => Object.fromEntries(
          Object.entries(results).map(([task, messages]) => [task, messages ?? previous[task] ?? null]),
        ))
      }
      if (!cancelled) timer = setTimeout(refresh, 30000)
    }
    void refresh()
    return () => { cancelled = true; clearTimeout(timer) }
  }, [messagesView, taskIds])
  const query = search.trim().toLowerCase()
  const allJobs = state.data?.jobs ?? []
  const jobs = allJobs.filter(job => [job.title, job.agent_name, job.task_id, job.job_id, job.program, job.status, job.blocker].some(value => value?.toLowerCase().includes(query)))
  // Only the website task's current attempt requires action. Imported history
  // can have newer insertion timestamps than a completed replacement.
  const latestByTask = new Map<string, string>()
  for (const job of allJobs) {
    if (job.current_for_task === true) latestByTask.set(job.task_id, job.job_id)
    else if (job.current_for_task === undefined && !latestByTask.has(job.task_id)) latestByTask.set(job.task_id, job.job_id)
  }
  const pending = jobs.filter(job => isPending(job.status))
  const active = jobs.filter(job => isLive(job.status))
  const attention = jobs.filter(job => latestByTask.get(job.task_id) === job.job_id && needsAttention(job))
  const history = jobs.filter(job => job.status === 'succeeded' || job.status === 'cancelled' ||
    (latestByTask.get(job.task_id) !== job.job_id && needsAttention(job)))
  const previousIds = new Set(history.filter(job => latestByTask.get(job.task_id) !== job.job_id).map(job => job.job_id))
  return <div className="agents-page">
    <div className="page-head"><h1>Agents</h1>
      <nav className="agents-view-switch segmented" aria-label="Agents view">
        <NavLink end to="/eng/agents" className={({ isActive }) => isActive || !messagesView ? 'selected' : ''}><Icon name="monitor" size={14} />Monitor</NavLink>
        <NavLink to="/eng/agents/messages" className={({ isActive }) => isActive ? 'selected' : ''}><Icon name="chat" size={14} />Messages</NavLink>
      </nav>
    </div>
    {messagesView ? <AgentMessages jobs={allJobs} /> : <>
    {state.error && <div className="note err">{state.error}</div>}
    {state.loading && !state.data && <p className="muted">Loading agents…</p>}
    {state.data && !state.error && <>
    <div className="experiments-toolbar">
      <h2>Verifier builds</h2>
      <label className="list-search"><Icon name="search" size={17} /><input type="search" aria-label="Search agents" placeholder="Search agents or tasks…" value={search} onChange={event => setSearch(event.target.value)} /></label>
    </div>
    {pending.length > 0 && <section className="experiments-section" aria-labelledby="pending-agents">
      <div className="experiments-section-head"><h3 id="pending-agents">Pending requests <span>{pending.length}</span></h3></div>
      <p>These requests have not been acknowledged by the authoring service. No running agent is implied.</p>
      <AgentHistory jobs={pending} messagesByTask={messagesByTask} />
    </section>}
    <section className="experiments-section" aria-labelledby="active-agents">
      <div className="experiments-section-head"><h3 id="active-agents">Active <span>{active.length}</span></h3><span className="experiments-refresh" title="Refreshes every 15 seconds">Auto-updating</span></div>
      {active.length ? <div className="active-experiments">{active.map(job => <ActiveAgent key={job.job_id} job={job} now={Date.now()} messages={messagesByTask[job.task_id]} />)}</div> : <p className="experiments-empty">{query ? 'No matching active builds.' : 'No agents are building right now.'}</p>}
    </section>
    {attention.length > 0 && <section className="experiments-section" aria-labelledby="attention-agents">
      <div className="experiments-section-head"><h3 id="attention-agents">Needs attention <span>{attention.length}</span></h3></div>
      <AgentHistory jobs={attention} messagesByTask={messagesByTask} />
    </section>}
    <section className="experiments-section" aria-labelledby="past-agents">
      <div className="experiments-section-head"><h3 id="past-agents">Past attempts <span>{history.length}</span></h3></div>
      {history.length ? <AgentHistory jobs={history} previousIds={previousIds} messagesByTask={messagesByTask} /> : <p className="experiments-empty">{query ? 'No matching past attempts.' : 'Past attempts will appear here.'}</p>}
    </section>
    {taskId && jobId && <AgentPanel key={`${taskId}/${jobId}`} taskId={taskId} jobId={jobId} summary={allJobs.find(job => job.job_id === jobId && job.task_id === taskId)} onClose={closePanel} />}
    </>}
    </>}
  </div>
}
